import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, Spinner } from 'react-bootstrap';
import { adminApi } from '../api/client';

/**
 * Read-only admin display of the campaign managers currently assigned
 * to a candidate. Editing happens exclusively on the Users page (when
 * adding or editing a campaign_manager). This component only resolves
 * user ids → user records and renders a small list.
 */
export default function AssignedManagersReadOnly({ assignedIds = [] }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const ids = (assignedIds || []).filter(Boolean);
    if (ids.length === 0) {
      setUsers([]);
      return undefined;
    }
    let active = true;
    setLoading(true);
    (async () => {
      try {
        // Fetch them one by one — endpoint already returns full shape with
        // role/active flags. For 1-5 managers per candidate this is fine.
        const out = await Promise.all(ids.map(async (id) => {
          try {
            const res = await adminApi.get(`/admin/users/${id}`);
            return res.data;
          } catch (_e) {
            return { id, email: 'unknown', is_active: false };
          }
        }));
        if (active) setUsers(out);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [assignedIds]);

  return (
    <Card data-testid="assigned-managers-readonly">
      <Card.Header className="d-flex justify-content-between align-items-center">
        <span>Assigned campaign managers</span>
        <Link to="/admin/users?role=campaign_manager" className="text-decoration-none" style={{ fontSize: '0.78rem' }}>
          Manage in Users →
        </Link>
      </Card.Header>
      <Card.Body>
        {loading ? (
          <div className="text-center py-2"><Spinner animation="border" size="sm" /></div>
        ) : users.length === 0 ? (
          <div className="text-muted" style={{ fontSize: '0.88rem' }} data-testid="assigned-managers-empty">
            No campaign managers are assigned to this candidate yet.
          </div>
        ) : (
          <ul className="list-unstyled mb-0" data-testid="assigned-managers-list">
            {users.map((u) => (
              <li key={u.id} className="d-flex justify-content-between align-items-center py-1" data-testid={`assigned-manager-${u.id}`}>
                <span>
                  <Link to={`/admin/users/${u.id}`} className="text-decoration-none fw-semibold">
                    {u.full_name || `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email}
                  </Link>
                  <span className="text-muted ms-2" style={{ fontSize: '0.78rem' }}>{u.email}</span>
                </span>
                {u.is_active === false && (
                  <span className="badge bg-warning text-dark">Disabled</span>
                )}
              </li>
            ))}
          </ul>
        )}
        <div className="text-muted mt-2" style={{ fontSize: '0.78rem' }}>
          Each campaign manager is assigned to one candidate from their user profile.
        </div>
      </Card.Body>
    </Card>
  );
}
